import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useCalendar } from '../../hooks/useCalendar';
import { Eye, EyeOff } from 'lucide-react';

const CalendarConnectionDialog = ({ show, onHide, onSuccess, workspaceId }) => {
    const {
        connectExternalCalendar,
        loading: hookLoading,
        error: hookError,
    } = useCalendar({ workspaceId });

    const [formData, setFormData] = useState({
        provider: 'other', // google, icloud, samsung, microsoft, other
        serverUrl: '',
        username: '',
        password: '',
    });

    const [settings, setSettings] = useState({
        obfuscate: false,
        color: '#ff0000'
    });

    const [showPassword, setShowPassword] = useState(false);
    const [localError, setLocalError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalError(null);

        try {
            const payload = {
                workspaceId,
                ...formData,
                settings
            };

            await connectExternalCalendar(payload);

            // successful connection
            setFormData({ provider: 'other', serverUrl: '', username: '', password: '' });
            if (onSuccess) onSuccess();
            onHide();
        } catch (err) {
            console.error(err);
            setLocalError(err.message || "Failed to connect calendar");
        }
    };

    const renderProviderHelp = () => {
        switch (formData.provider) {
            case 'icloud':
                return (
                    <Alert variant="info" className="mt-2 text-wrap">
                        <strong>How to connect iCloud:</strong>
                        <ol className="mb-0 ps-3">
                            <li>Go to <a href="https://appleid.apple.com" target="_blank" rel="noreferrer">appleid.apple.com</a> and sign in.</li>
                            <li>Go to "Sign-In and Security" and generate an <strong>App-Specific Password</strong>.</li>
                            <li>Use the email assosiated with your Apple ID as the username.</li>
                            <li>Use `https://caldav.icloud.com` as the Server URL.</li>
                        </ol>
                    </Alert>
                );
            case 'samsung':
                return (
                    <Alert variant="info" className="mt-2 text-wrap">
                        <strong>How to connect Samsung Calendar:</strong>
                        <p className="mb-2">Samsung Calendar usually syncs data from Google or Microsoft accounts. We recommend connecting those accounts directly.</p>
                        <p className="mb-0">If you are using Samsung Cloud directly, getting a CalDAV URL is difficult. Try syncing your Samsung Calendar to Google Calendar first, then connect Google here.</p>
                    </Alert>
                );
            case 'google':
                return (
                    <Alert variant="info" className="mt-2 text-wrap">
                        <strong>How to connect Google Calendar:</strong>
                        <ol className="mb-0 ps-3">
                            <li>Go to <strong>Google Calendar Settings</strong>.</li>
                            <li>Select your Calendar and scroll to "Secret address in iCal format".</li>
                            <li>Copy and paste that URL below.</li>
                        </ol>
                        <p className="mt-2 mb-0" style={{ fontSize: '0.85rem' }}><em>This provides a Read-Only sync.</em></p>
                    </Alert>
                );
            case 'microsoft':
                return (
                    <Alert variant="info" className="mt-2 text-wrap">
                        <strong>How to connect Outlook / Office 365:</strong>
                        <ol className="mb-0 ps-3">
                            <li>Go to <strong>Outlook.com (Web) Settings {'>'} Calendar {'>'} Shared calendars</strong>.</li>
                            <li>Under "Publish a calendar", select the calendar and permissions.</li>
                            <li>Click "Publish" and copy the <strong>ICS</strong> link.</li>
                            <li>Paste the ICS link below.</li>
                        </ol>
                        <p className="mt-2 mb-0" style={{ fontSize: '0.85rem' }}><em>This provides a Read-Only sync.</em></p>
                    </Alert>
                );
            case 'other':
            default:
                return (
                    <Alert variant="info" className="mt-2 text-wrap">
                        <strong>Generic CalDAV:</strong>
                        <ul className="mb-0 ps-3">
                            <li>Enter the full <strong>CalDAV URL</strong> provided by your host.</li>
                            <li>Use your standard username and password (or app token).</li>
                        </ul>
                    </Alert>
                );
        }
    };

    const isSecretUrlProvider = formData.provider === 'google' || formData.provider === 'microsoft';

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Link External Calendar</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {(hookError || localError) && <Alert variant="danger">{hookError || localError}</Alert>}

                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Provider</Form.Label>
                        <Form.Select
                            value={formData.provider}
                            onChange={e => setFormData({ ...formData, provider: e.target.value })}
                        >
                            <option value="other">CalDAV (Generic)</option>
                            <option value="icloud">Apple iCloud</option>
                            <option value="google">Google Calendar</option>
                            <option value="microsoft">Microsoft Outlook / 365</option>
                            <option value="samsung">Samsung Calendar</option>
                        </Form.Select>
                        {renderProviderHelp()}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Server URL (CalDAV or iCal)</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={formData.provider === 'icloud' ? "https://caldav.icloud.com" : "https://..."}
                            value={formData.serverUrl}
                            onChange={e => setFormData({ ...formData, serverUrl: e.target.value })}
                            required
                        />
                        {isSecretUrlProvider && (
                            <Form.Text className="text-muted">
                                Paste the ICS/Secret Address here.
                            </Form.Text>
                        )}
                    </Form.Group>

                    {!isSecretUrlProvider && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Username / Email</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.username}
                                    onChange={e => setFormData({ ...formData, username: e.target.value })}
                                    required={!isSecretUrlProvider}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>App-Specific Password</Form.Label>
                                <div className="input-group">
                                    <Form.Control
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                                        required={!isSecretUrlProvider}
                                    />
                                    <Button variant="outline-secondary" onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </Button>
                                </div>
                            </Form.Group>
                        </>
                    )}

                    <div className="text-muted small mt-2 mb-3">
                        <i className="bi bi-shield-lock-fill me-1"></i>
                        Your credentials are securely encrypted and stored in <strong>AWS Secrets Manager</strong>.
                    </div>

                    <hr />
                    <h5>Privacy & Sync</h5>
                    <Form.Check
                        type="switch"
                        id="obfuscate-switch"
                        label="Obfuscate Event Details (Display as 'Blocked')"
                        checked={settings.obfuscate}
                        onChange={e => setSettings({ ...settings, obfuscate: e.target.checked })}
                        className="mb-3"
                    />

                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="secondary" onClick={onHide}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={hookLoading}>
                            {hookLoading ? <Spinner size="sm" animation="border" className="me-2" /> : null}
                            Connect Calendar
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal >
    );
};

export default CalendarConnectionDialog;
